/**
 * AK-257 — TanStack Query hooks for per-culture reference images + the
 * affected-crops write-path. Generic over the dictionary (diseases/pests/weeds)
 * so the same hooks back all three editor drawers.
 *
 * Endpoints (reference.py, AK-257):
 *   GET    /reference/{dict}/{id}/crops
 *   PUT    /reference/{dict}/{id}/crops[?force=true]
 *   GET    /reference/{dict}/{id}/images
 *   POST   /reference/{dict}/{id}/images        (multipart: file + crop_ids)
 *   PATCH  /reference/{dict}/{id}/images/{imgId} ({crop_ids})
 *   DELETE /reference/{dict}/{id}/images/{imgId}
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AffectedCrop,
  ImageDict,
  ReferenceImage,
} from '@/pages/platform-admin/reference/types';

export const imageBankKeys = {
  crops: (dict: ImageDict, id: string) =>
    ['reference', dict, 'affected-crops', id] as const,
  images: (dict: ImageDict, id: string) =>
    ['reference', dict, 'images', id] as const,
};

// ---------- Affected crops ----------

export function useAffectedCrops(
  dict: ImageDict,
  entityId: string | null,
): UseQueryResult<AffectedCrop[], Error> {
  return useQuery({
    queryKey: imageBankKeys.crops(dict, entityId ?? ''),
    queryFn: () =>
      api.get<AffectedCrop[]>(`/api/v1/reference/${dict}/${entityId}/crops`),
    enabled: Boolean(entityId),
    staleTime: 15_000,
  });
}

export interface ReplaceCropsInput {
  entityId: string;
  crops: { crop_id: string; severity?: string | null }[];
  /** Q3 — confirm removal of a crop that still has photos (orphans deleted, D1). */
  force?: boolean;
}

/**
 * Replace-set the affected crops. On a 409 {code:"crop_has_images"} the caller
 * is expected to show a confirm dialog and retry with force=true. We surface the
 * ApiError (rawDetail carries the code) rather than swallowing it.
 */
export function useReplaceAffectedCrops(dict: ImageDict) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, crops, force }: ReplaceCropsInput) =>
      api.put<AffectedCrop[]>(
        `/api/v1/reference/${dict}/${entityId}/crops${force ? '?force=true' : ''}`,
        { crops },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: imageBankKeys.crops(dict, vars.entityId) });
      qc.invalidateQueries({ queryKey: imageBankKeys.images(dict, vars.entityId) });
    },
  });
}

// ---------- Images ----------

export function useReferenceImages(
  dict: ImageDict,
  entityId: string | null,
): UseQueryResult<ReferenceImage[], Error> {
  return useQuery({
    queryKey: imageBankKeys.images(dict, entityId ?? ''),
    queryFn: () =>
      api.get<ReferenceImage[]>(`/api/v1/reference/${dict}/${entityId}/images`),
    enabled: Boolean(entityId),
    staleTime: 15_000,
  });
}

export interface UploadImageInput {
  entityId: string;
  file: File;
  cropIds: string[];
}

export function useUploadReferenceImage(dict: ImageDict) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, file, cropIds }: UploadImageInput) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('crop_ids', cropIds.join(','));
      return api.uploadFile<ReferenceImage>(
        `/api/v1/reference/${dict}/${entityId}/images`,
        fd,
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: imageBankKeys.images(dict, vars.entityId) });
      qc.invalidateQueries({ queryKey: imageBankKeys.crops(dict, vars.entityId) });
    },
  });
}

export interface UpdateImageCropsInput {
  entityId: string;
  imageId: string;
  cropIds: string[];
}

export function useUpdateImageCrops(dict: ImageDict) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, imageId, cropIds }: UpdateImageCropsInput) =>
      api.patch<ReferenceImage>(
        `/api/v1/reference/${dict}/${entityId}/images/${imageId}`,
        { crop_ids: cropIds },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: imageBankKeys.images(dict, vars.entityId) });
      qc.invalidateQueries({ queryKey: imageBankKeys.crops(dict, vars.entityId) });
    },
  });
}

export interface DeleteImageInput {
  entityId: string;
  imageId: string;
}

export function useDeleteReferenceImage(dict: ImageDict) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, imageId }: DeleteImageInput) =>
      api.delete<void>(`/api/v1/reference/${dict}/${entityId}/images/${imageId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: imageBankKeys.images(dict, vars.entityId) });
      qc.invalidateQueries({ queryKey: imageBankKeys.crops(dict, vars.entityId) });
    },
  });
}
